import { useEffect, type FC, useMemo, useRef, useState } from 'react'
import whatsapp from '~/assets/whatsapp.svg'
import facebook from '~/assets/facebook.svg'
import twitter from '~/assets/twitter.svg'
import mail from '~/assets/mail.svg'
import copy from '~/assets/copy.svg'
import givematchlogo from '~/assets/givematch-logo.svg'
import { AdditionalContribution, SplitType, totalQurbaniDonation, calculateDhulHijjahStartDate, formatDate, calculateDhulHijjahDuration, mtdSplitTexts, useSharePageDetails, type Plan, useCheckoutPlan, optIntoGivematch, MTDtracking, handleShare } from '@shamaazi/mytennights'
import { Choice, displayCurrency, UrlRoutes } from '@lib/services'
import { Button } from '@lib/components'
import { useAuth } from '@lib/hooks'

interface ShareButtonsProps {
  shareUrl: string
}
enum ShareType {
  COPY = 'copy',
  WHATSAPP = 'whatsapp',
  FACEBOOK = 'facebook',
  TWITTER = 'twitter',
  MAIL = 'mail',
}

const ShareButtons: FC<ShareButtonsProps> = ({ shareUrl }) => {
  const { user } = useAuth()
  return (
    <>
      <div className='flex items-center mb-4 gap-x-2 w-full'>
        <input
          type="text"
          value={shareUrl}
          readOnly
          className='font-montserrat flex-1 border text-base border-mtd-gray-4 p-3 text-mtd-gray-6 rounded-lg'
        />
        <button
          type="button"
          className='bg-gm-newgreen text-white w-12 h-12 flex items-center justify-center rounded-full'
          onClick={() => {
            MTDtracking.socialShareButton(user?.email, ShareType.COPY, false, shareUrl)
            navigator.clipboard.writeText(shareUrl).catch(err => {
              console.error('Failed to copy text: ', err)
            })
          }}
        >
          <img alt={ShareType.COPY} src={copy} className='w-6 h-6' />
        </button>
      </div>
      <div className="flex justify-between w-full">
        <button
          type="button"
          className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center"
          onClick={() => {
            MTDtracking.socialShareButton(user?.email, ShareType.WHATSAPP, false, shareUrl)
            handleShare(ShareType.WHATSAPP, shareUrl)
          }}
        >
          <img alt={ShareType.WHATSAPP} src={whatsapp} className="w-[18px] h-[18px]" />
        </button>
        <button
          type="button"
          className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center"
          onClick={() => {
            MTDtracking.socialShareButton(user?.email, ShareType.FACEBOOK, false, shareUrl)
            handleShare(ShareType.FACEBOOK, shareUrl)
          }}
        >
          <img alt={ShareType.FACEBOOK} src={facebook} className="w-[18px] h-[18px]" />
        </button>
        <button
          type="button"
          className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center"
          onClick={() => {
            MTDtracking.socialShareButton(user?.email, ShareType.TWITTER, false, shareUrl)
            handleShare(ShareType.TWITTER, shareUrl)
          }}
        >
          <img alt={ShareType.TWITTER} src={twitter} className="w-[18px] h-[18px]" />
        </button>
        <button
          type="button"
          className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center"
          onClick={() => {
            MTDtracking.socialShareButton(user?.email, ShareType.MAIL, false, shareUrl)
            handleShare(ShareType.MAIL, shareUrl)
          }}
        >
          <img alt={ShareType.MAIL} src={mail} className="w-[18px] h-[18px]" />
        </button>
      </div>
    </>
  )
}

export const Confirmation: FC = () => {
  const { plan } = useCheckoutPlan()

  const { sharePageDetails } = useSharePageDetails()

  const [isShareButtonClicked, setIsShareButtonClicked] = useState(false)

  const shareData: Plan = useMemo(() => ({
    ...plan,
    causes: sharePageDetails.causes ?? plan.causes,
    charity: sharePageDetails.charity ?? plan.charity,
    split: sharePageDetails.split ?? plan.split,
    fridayGiving: sharePageDetails.fridayGiving ?? plan.fridayGiving,
    fridayGivingOptIn: sharePageDetails.fridayGivingOptIn ?? plan.fridayGivingOptIn
  }), [plan, sharePageDetails.causes, sharePageDetails.charity, sharePageDetails.split, sharePageDetails.fridayGiving, sharePageDetails.fridayGivingOptIn])

  const shareUrl = useMemo(() => UrlRoutes.buildShareUrl(sharePageDetails.shareCode), [sharePageDetails.shareCode])
  const receipt = useMemo(() => ({
    charity: shareData.charity.charity_name,
    startDate: formatDate(calculateDhulHijjahStartDate().toISODate()),
    duration: calculateDhulHijjahDuration(),
    donationSplit: shareData.split === SplitType.MoreArafah ? `${mtdSplitTexts[SplitType.MoreArafah]} (x2)` : `${mtdSplitTexts[SplitType.Equally]}`,
    qurbani: displayCurrency({ amount: totalQurbaniDonation(shareData), currency: shareData.charity.currency }),
    paymentMethod: 'Card',
    total: displayCurrency({ amount: sharePageDetails.total, currency: shareData.charity.currency })
  }), [shareData, sharePageDetails.total])

  const hasTracked = useRef(false)

  useEffect(() => {
    if (!hasTracked.current && sharePageDetails.email && shareUrl && receipt) {
      MTDtracking.ConfirmationPage(sharePageDetails.email, 'SharePageLoaded', false, shareUrl, receipt)
      hasTracked.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleShareButtonClick = (): void => {
    setIsShareButtonClicked(true)
    optIntoGivematch(sharePageDetails.shareCode).catch(() => {})
  }

  return (
    <>
      <div className="bg-mtd-bg mx-auto flex flex-col items-center">
      <div className="items-center pt-9 bg-mtd-bg w-fg-width-400">
        <div className="bg-white rounded-xl pt-9 pb-6 px-5 w-full mb-6 flex flex-col items-center border border-mtd-gray-3">
          <div className="text-xl mb-4 items-center w-full">
            <img src={givematchlogo} alt='givematchlogo' className='w-full h-4' />
          </div>
          <div className="text-gm-newgreen font-poppins font-extrabold text-2xl text-center leading-tight w-full">
            Your {displayCurrency({ amount: sharePageDetails.totalCauseDonation ?? 0, currency: shareData.charity.currency })} will
            <br />
            become {displayCurrency({ amount: (sharePageDetails.totalCauseDonation ?? 0) * 2, currency: shareData.charity.currency })}
          </div>
          <div className="text-gm-green-950 font-extrabold font-poppins text-2xl text-center mb-4 w-full">
            when you get
            <br />2 friends to donates
          </div>
          {isShareButtonClicked ? (
            <ShareButtons shareUrl={shareUrl} />
          ) : (
            <Button className="w-full text-xl" variant='gm-primary-newgreen' onClick={handleShareButtonClick}>
              Share
            </Button>
          )}
        </div>

        {/* Receipt Card */}
        <div className="bg-white font-montserrat text-mtd-secondary-5 rounded-2xl py-5 px-4 w-full max-w-md mb-6 border border-mtd-gray-3">
          <div className="mb-2">
            <div className="font-bold text-lg">My receipt</div>
            <div className="text-sm font-semibold">
              to {receipt.charity}
            </div>
          </div>
          <div className="mt-4 space-y-2 text-mtd-secondary-4">
            <div className="flex justify-between text-sm">
              <span>Start Date</span>
              <span className="font-semibold">
                {receipt.startDate}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Duration</span>
              <span className="font-semibold">
                {receipt.duration}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Donation split</span>
              <span className="font-semibold">
                {receipt.donationSplit}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Qurbani</span>
              <span className="font-semibold">
                {receipt.qurbani}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Payment Method</span>
              <span className="font-semibold">
                {receipt.paymentMethod}
              </span>
            </div>
            </div>
            <div className="pt-5">
              <div className="border-t border-mtd-secondary-5 opacity-20 pb-4"/>
              <div className="flex justify-between items-center text-mtd-secondary-5">
                <span className="font-bold text-lg">TOTAL</span>
                <span className="font-bold text-lg">{receipt.total}</span>
              </div>
          </div>
        </div>
        {shareData.charity && sharePageDetails?.fridayGivingOptIn === Choice.yes && <AdditionalContribution isFG={true} plan={shareData} />}
        <div className="text-center font-montserrat text-xs mt-3 text-fg-blue-800">
          Check your email to manage subscription.
        </div>
        </div>
        </div>
    </>
  )
}

export default Confirmation
